// src/pages/analytics/payables/AgingTab.tsx
//
// Open-document aging for A/P (QuickBooks Online open bills, GET /api/qbo/ap-aging —
// Advantage records no bill payments) and A/R (recipe ar-aging),
// agent 3.24.0+. Toggle: by bill/invoice date (0–30 … 120+) or by days past
// due (Not due, 1–30 … 90+). Due date = date + the number in Terms. Party
// table → click a row to see its open documents; payment/collection schedule
// tiles on top.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarClock, RefreshCw, Search, X } from 'lucide-react';
import { apiV2 as api } from '../../../services/apiV2';
import type { AgingBasis, AgingDoc, AgingParty, AgingResponse } from '../../../types/payables';
import { exportCsv, type CsvColumns } from '../../../lib/csvExport';
import { exportXlsx } from '../../../lib/xlsxExport';
import {
  ExportButtons, LoadError, Spinner, Tile, controlsCls, fmtCur, fmtCur2, labelCls, pill,
  tableWrap, tdCls, tdR, thCls, thR,
} from './apShared';
import { qboOff } from './QboDiffTab';

export function AgingTab({ kind }: { kind: 'ap' | 'ar' }) {
  const isAp = kind === 'ap';
  const [basis, setBasis] = useState<AgingBasis>('billDate');
  const [search, setSearch] = useState('');
  const [party, setParty] = useState<AgingParty | null>(null);
  const [pastDueOnly, setPastDueOnly] = useState(false);

  const q = useQuery({
    queryKey: ['analyticsAging', kind, basis],
    queryFn: () => isAp ? api.getApAging({ basis }) : api.getArAging({ basis }),
    staleTime: 60_000,
    retry: 0,
  });
  const data: AgingResponse | undefined = q.data;
  const docLabel = isAp ? 'Bill' : 'Invoice';
  const partyLabel = isAp ? 'Vendor' : 'Customer';
  const dateLabel = isAp ? 'bill date' : 'invoice date';

  const parties = useMemo(() => {
    const s = search.trim().toLowerCase();
    let rows = data?.parties ?? [];
    if (s) rows = rows.filter(p => p.name.toLowerCase().includes(s) || String(p.code).includes(s));
    if (pastDueOnly) rows = rows.filter(p => p.pastDue > 0.005);
    return rows;
  }, [data, search, pastDueOnly]);

  const docs = useMemo(() => (data?.docs ?? []).filter(d => !party || d.code === party.code), [data, party]);
  const labels = data?.bucketLabels ?? [];

  const partyCols: CsvColumns<AgingParty> = [
    ['Code', r => r.code], [partyLabel, r => r.name], ['Balance', r => r.balance],
    ...labels.map((l, i) => [l, (r: AgingParty) => r.buckets[i]] as [string, (r: AgingParty) => number]),
    ['Past due', r => r.pastDue], ['Open docs', r => r.count], ['Oldest (days)', r => r.oldestDays],
  ];
  const docCols: CsvColumns<AgingDoc> = [
    [docLabel, r => r.doc], ...(isAp ? [['Vendor invoice #', (r: AgingDoc) => r.ref] as [string, (r: AgingDoc) => string]] : []),
    ['Code', r => r.code], [partyLabel, r => r.name], ['Date', r => r.date], ['Due', r => r.due], ['Terms', r => r.terms],
    ['Total', r => r.total], ['Paid', r => r.paid], ['Balance', r => r.balance], ['Age (days)', r => r.ageDays],
    ['Days past due', r => r.daysPastDue], [isAp ? 'Check #' : 'Salesman', r => r.extra],
  ];
  const fileBase = `${kind}-aging-${basis === 'dueDate' ? 'past-due' : 'by-date'}`;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <CalendarClock size={20} /> {isAp ? 'A/P Aging' : 'A/R Aging by Date'}
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Open {isAp ? 'vendor bills in QuickBooks Online' : 'invoices'} aged {basis === 'dueDate' ? 'by days past the due date' : `from the ${dateLabel}`}
          {data && <span className="text-slate-500"> · as of {data.asOf}</span>}
        </p>
      </div>

      <div className={controlsCls}>
        <div className="flex flex-col gap-1">
          <span className={labelCls}>Age by</span>
          <div className="flex gap-1">
            <button className={pill(basis === 'billDate')} onClick={() => setBasis('billDate')}>{isAp ? 'Bill date' : 'Invoice date'}</button>
            <button className={pill(basis === 'dueDate')} onClick={() => setBasis('dueDate')}>Past due</button>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className={labelCls}>{partyLabel}</span>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search ${partyLabel.toLowerCase()}…`}
              className="pl-6 pr-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white w-52 focus:outline-none focus:ring-1 focus:ring-blue-500/50" />
          </div>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-slate-300 pb-1.5">
          <input type="checkbox" checked={pastDueOnly} onChange={e => setPastDueOnly(e.target.checked)} /> Past due only
        </label>
        <div className="flex-1" />
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {q.error && (isAp && qboOff(q.error)
        ? <div className="p-4 rounded-md bg-slate-800/40 border border-slate-700/40 text-slate-300 text-sm">
            A/P aging comes from QuickBooks Online (bills are paid there, not in Advantage). {qboOff(q.error)}
          </div>
        : <LoadError error={q.error} what={`the ${isAp ? 'A/P' : 'A/R'} aging`} />)}

      {q.isLoading ? <Spinner /> : !q.error && data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Tile tone="violet" label={isAp ? 'Open A/P' : 'Open A/R'} value={fmtCur(data.totals.balance)}
              sub={`${data.totals.count} ${isAp ? 'bills' : 'invoices'} · ${data.totals.parties} ${partyLabel.toLowerCase()}s`} />
            <Tile tone="red" label="Past due" value={fmtCur(data.schedule.pastDue)}
              sub={data.totals.balance ? `${(data.schedule.pastDue / data.totals.balance * 100).toFixed(1)}% of open` : undefined} />
            <Tile label="Due in 7 days" value={fmtCur(data.schedule.due0to7)} />
            <Tile label="Due 8–14 days" value={fmtCur(data.schedule.due8to14)} />
            <Tile label="Due 15–30 days" value={fmtCur(data.schedule.due15to30)} />
            <Tile label="Due later" value={fmtCur(data.schedule.later)}
              sub={data.totals.credits < 0 ? `incl. ${fmtCur(data.totals.credits)} credits` : undefined} />
          </div>

          {/* Bucket strip */}
          <div className="grid grid-cols-5 gap-2">
            {labels.map((l, i) => (
              <div key={l} className="rounded-lg border border-slate-700/40 bg-slate-900/40 p-3">
                <div className="text-[11px] text-slate-400">{l}{basis === 'billDate' ? ' days' : ''}</div>
                <div className={`text-base font-semibold tabular-nums ${i >= 3 ? 'text-red-300' : i >= 1 && basis === 'dueDate' ? 'text-amber-300' : 'text-white'}`}>
                  {fmtCur(data.totals.buckets[i] ?? 0)}
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-200">By {partyLabel.toLowerCase()} <span className="text-slate-500 font-normal">({parties.length})</span></h3>
              <div className="flex gap-1">
                <ExportButtons what={`${partyLabel.toLowerCase()} aging`} disabled={!parties.length}
                  onCsv={() => exportCsv(fileBase, partyCols, parties)} onXlsx={() => exportXlsx(fileBase, partyCols, parties)} />
              </div>
            </div>
            <div className={`${tableWrap} max-h-[420px]`}>
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-900">
                  <tr>
                    <th className={thCls}>{partyLabel}</th>
                    <th className={thR}>Balance</th>
                    {labels.map(l => <th key={l} className={thR}>{l}</th>)}
                    <th className={thR}>Past due</th>
                    <th className={thR}>Docs</th>
                    <th className={thR}>Oldest</th>
                  </tr>
                </thead>
                <tbody>
                  {parties.map(p => (
                    <tr key={p.code} onClick={() => setParty(party?.code === p.code ? null : p)}
                      className={`border-t border-slate-800/60 cursor-pointer hover:bg-slate-800/40 ${party?.code === p.code ? 'bg-blue-500/10' : ''}`}>
                      <td className={tdCls}><span className="text-slate-500 mr-1.5">{p.code}</span>{p.name}</td>
                      <td className={`${tdR} font-medium`}>{fmtCur2(p.balance)}</td>
                      {p.buckets.map((b, i) => <td key={i} className={`${tdR} ${b === 0 ? 'text-slate-600' : i >= 3 ? 'text-red-300' : ''}`}>{b === 0 ? '–' : fmtCur2(b)}</td>)}
                      <td className={`${tdR} ${p.pastDue > 0 ? 'text-red-300' : 'text-slate-600'}`}>{p.pastDue > 0 ? fmtCur2(p.pastDue) : '–'}</td>
                      <td className={tdR}>{p.count}</td>
                      <td className={tdR}>{p.oldestDays}d</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
                Open {isAp ? 'bills' : 'invoices'} {party && <>— {party.name}
                  <button onClick={() => setParty(null)} className="text-slate-500 hover:text-white"><X size={12} /></button></>}
                <span className="text-slate-500 font-normal">({docs.length}{data.docsTruncated ? ', first 5,000' : ''})</span>
              </h3>
              <div className="flex gap-1">
                <ExportButtons what={`open ${isAp ? 'bills' : 'invoices'}`} disabled={!docs.length}
                  onCsv={() => exportCsv(`${fileBase}-docs`, docCols, docs)} onXlsx={() => exportXlsx(`${fileBase}-docs`, docCols, docs)} />
              </div>
            </div>
            <div className={`${tableWrap} max-h-[480px]`}>
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-900">
                  <tr>
                    <th className={thCls}>{docLabel}</th>
                    {isAp && <th className={thCls}>Vendor inv #</th>}
                    <th className={thCls}>{partyLabel}</th>
                    <th className={thCls}>Date</th>
                    <th className={thCls}>Due</th>
                    <th className={thCls}>Terms</th>
                    <th className={thR}>Total</th>
                    <th className={thR}>Paid</th>
                    <th className={thR}>Balance</th>
                    <th className={thR}>Age</th>
                    <th className={thR}>Past due</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map(d => (
                    <tr key={`${d.code}-${d.doc}`} className="border-t border-slate-800/60">
                      <td className={tdCls}>{d.doc}</td>
                      {isAp && <td className={`${tdCls} text-slate-400`}>{d.ref}</td>}
                      <td className={tdCls}>{d.name}</td>
                      <td className={tdCls}>{d.date}</td>
                      <td className={tdCls}>{d.due}</td>
                      <td className={`${tdCls} text-slate-400`}>{d.terms || '–'}</td>
                      <td className={tdR}>{fmtCur2(d.total)}</td>
                      <td className={tdR}>{fmtCur2(d.paid)}</td>
                      <td className={`${tdR} font-medium ${d.balance < 0 ? 'text-emerald-300' : ''}`}>{fmtCur2(d.balance)}</td>
                      <td className={tdR}>{d.ageDays}d</td>
                      <td className={`${tdR} ${d.daysPastDue > 60 ? 'text-red-400' : d.daysPastDue > 0 ? 'text-amber-300' : 'text-slate-600'}`}>
                        {d.daysPastDue > 0 ? `${d.daysPastDue}d` : '–'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
